<template>
  <a-modal :visible="visible" :title="title" width="820px" :mask-closable="false" @cancel="handleCancel" okText="确认" cancelText="取消" destroyOnClose>
    <div class="searchArea">
      <a-input v-model:value="searchName" placeholder="请输入商品名称" allowClear style="width: 220px; margin-right: 10px;" @pressEnter="search" />
      <a-button type="primary" shape="circle" @click="search()">
        <template #icon>
          <SearchOutlined />
        </template>
      </a-button>
      <a-button style="margin-left: 8px" @click="resetSearch">重置</a-button>
    </div>

    <a-table class="ant-table-striped" :columns="columns" :data-source="data" :loading="loading"
      :row-selection="multiple ? rowSelection : null"
      :rowClassName="(record, index) => (index % 2 === 1 ? 'table-striped' : null)" bordered rowKey="id" :pagination="{ pageSize: 10 }" size="small">
      <template #thumbnailUuid="{ record }">
        <a-image v-if="record.thumbnailUuid" :src="imageUrl + record.thumbnailUuid" :width="40" :height="40" style="object-fit: cover; border-radius: 4px;" alt="缩略图" />
        <span v-else>-</span>
      </template>
      <template #operation="{ record }">
        <a-button type="link" size="small" @click="selectProduct(record)">选择</a-button>
      </template>
    </a-table>

    <template #footer>
      <div v-if="multiple" class="mult-select-footer">
        已选 <b>{{ selectedRowKeys.length }}</b> 项
        <a-button style="margin-left: 12px" :disabled="selectedRowKeys.length === 0" @click="clearSelection">清空</a-button>
        <a-button type="primary" :disabled="selectedRowKeys.length === 0" @click="confirmMultiple">确定</a-button>
      </div>
      <div v-else></div>
    </template>
  </a-modal>
</template>

<script>
import { SearchOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { GetProductList } from "/src/api/shopping.js";

export default {
  name: "选择商品弹窗",
  components: {
    SearchOutlined,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // 是否支持多选：true 时使用 checkbox + 底部“确定”按钮，false 时为原有单选（点击操作列“选择”即返回）
    multiple: {
      type: Boolean,
      default: false,
    },
    // 弹窗标题（默认与原单选弹窗一致，多选场景可传入自定义标题）
    title: {
      type: String,
      default: "选择跳转商品",
    },
    // 多选模式下，打开弹窗时需要回显勾选的已选商品记录（父组件传入当前已关联的商品）
    initialSelected: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:visible', 'select'],
  data() {
    return {
      searchName: undefined,
      data: [],
      loading: false,
      imageUrl: '/api/file/img/thumbnail/',
      selectedRowKeys: [],
      // 打开弹窗时记录初始已选对象，用于确认时补全不在当前列表里的记录
      selectedRecords: [],
    };
  },
  computed: {
    columns() {
      const base = [
        { title: "商品名称", dataIndex: "name", key: "name", width: "30%" },
        { title: "缩略图", dataIndex: "thumbnailUuid", key: "thumbnailUuid", width: "15%", slots: { customRender: "thumbnailUuid" } },
        { title: "类型", dataIndex: "category", key: "category", width: "15%" },
        { title: "单位", dataIndex: "packageUnit", key: "packageUnit", width: "10%" },
        { title: "价格", dataIndex: "price", key: "price", width: "10%" },
      ];
      if (!this.multiple) {
        base.push({ title: "操作", key: "operation", width: "15%", slots: { customRender: "operation" } });
      }
      return base;
    },
    rowSelection() {
      if (!this.multiple) return null;
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: (keys) => {
          this.selectedRowKeys = keys;
        },
      };
    },
  },
  watch: {
    // 打开弹窗时自动搜索商品列表，多选模式下回显勾选已选商品
    visible(val) {
      if (val) {
        if (this.multiple) {
          this.refreshSelected();
        }
        this.search();
      }
    },
  },
  methods: {
    // 多选模式下，按父级传入的已选商品初始化勾选状态
    refreshSelected() {
      const init = this.initialSelected || [];
      this.selectedRecords = init
        .map((r) => ({ ...r, id: r.id !== undefined ? r.id : r.uuid }))
        .filter((r) => r.id !== undefined && r.id !== null);
      this.selectedRowKeys = this.selectedRecords.map((r) => r.id);
    },

    // 重置搜索
    resetSearch() {
      this.searchName = undefined;
      this.search();
    },

    // 清空多选
    clearSelection() {
      this.selectedRowKeys = [];
    },

    // 关闭弹窗
    handleCancel() {
      this.$emit('update:visible', false);
    },

    // 单选模式：选择商品，把选中的记录抛给父组件
    selectProduct(record) {
      this.$emit('select', record);
      this.$emit('update:visible', false);
    },

    // 多选模式：确认，把选中的记录数组抛给父组件
    confirmMultiple() {
      const selected = this.selectedRowKeys.map((k) => {
        const cur = this.data.find((d) => d.id === k);
        if (cur) return cur;
        const saved = this.selectedRecords.find((r) => r.id === k);
        if (saved) return saved;
        return { id: k };
      });
      this.$emit('select', selected);
      this.$emit('update:visible', false);
    },

    // 查询商品列表
    async search() {
      this.loading = true;
      const params = { name: this.searchName };
      try {
        const res = await GetProductList(params);
        if (res.errCode === 0) {
          this.data = res.data || [];
        } else {
          message.error(res.errMsg || "查询失败");
        }
      } catch (error) {
        console.error("Search error:", error);
        message.error("请求异常，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.searchArea {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
</style>